import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, EnvelopeSimple, CheckCircle, Archive, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Admin } from "../../lib/api";

const STATUSES = ["", "new", "replied", "closed"];
const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

function MessageRow({ m, onStatus }) {
  const [open, setOpen] = useState(false);
  const chip = { new: "bg-[#FF2A2A] text-white", replied: "bg-[#002FA7] text-white", closed: "bg-neutral-300" }[m.status] || "";
  return (
    <div className="border-b border-neutral-300" data-testid={`admin-contact-row-${m.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left grid grid-cols-12 gap-2 items-center px-3 py-2 hover:bg-neutral-50" data-testid={`admin-contact-toggle-${m.id}`}>
        <div className="col-span-3 font-mono text-xs">{fmt(m.created_at)}</div>
        <div className="col-span-3 font-mono text-xs truncate" title={m.email}>{m.name ? `${m.name} · ` : ""}{m.email}</div>
        <div className="col-span-3 font-mono text-xs truncate">{m.topic}</div>
        <div className="col-span-2 font-mono text-xs truncate text-neutral-500">{m.message}</div>
        <div className="col-span-1 text-right"><span className={`label-mono text-[10px] px-1.5 py-0.5 ${chip}`} data-testid={`admin-contact-status-${m.id}`}>{m.status}</span></div>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3 bg-neutral-50" data-testid={`admin-contact-detail-${m.id}`}>
          <pre className="whitespace-pre-wrap font-mono text-sm border-2 border-black bg-white p-3">{m.message}</pre>
          <div className="flex flex-wrap items-center gap-2">
            <a href={`mailto:${m.email}?subject=${encodeURIComponent(`Re: ${m.topic} — AIImageSuites`)}`} className="btn-flat text-xs inline-flex items-center gap-1" data-testid={`admin-contact-reply-${m.id}`}>
              <EnvelopeSimple size={14} weight="bold" /> REPLY BY EMAIL
            </a>
            {m.status !== "replied" && (
              <button onClick={() => onStatus(m.id, "replied")} className="btn-flat-outline text-xs inline-flex items-center gap-1" data-testid={`admin-contact-mark-replied-${m.id}`}>
                <CheckCircle size={14} weight="bold" /> MARK REPLIED
              </button>
            )}
            {m.status !== "closed" && (
              <button onClick={() => onStatus(m.id, "closed")} className="btn-flat-outline text-xs inline-flex items-center gap-1" data-testid={`admin-contact-mark-closed-${m.id}`}>
                <Archive size={14} weight="bold" /> CLOSE
              </button>
            )}
            {m.status !== "new" && (
              <button onClick={() => onStatus(m.id, "new")} className="btn-flat-outline text-xs inline-flex items-center gap-1" data-testid={`admin-contact-reopen-${m.id}`}>
                <ArrowCounterClockwise size={14} weight="bold" /> REOPEN
              </button>
            )}
            <span className="label-mono text-[10px] text-neutral-500 ml-auto">
              {m.delivery?.forwarded ? "FORWARDED TO INBOX" : `NOT EMAILED (${m.delivery?.reason || "inbox not configured"})`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ContactInboxPanel({ onAuthFail }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await Admin.listContact(status);
      setItems(r.items || []);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("UNAUTHORIZED"); onAuthFail(); return; }
      toast.error("LOAD FAILED");
    } finally {
      setLoading(false);
    }
  };

  const setMsgStatus = async (id, s) => {
    try {
      await Admin.setContactStatus(id, s);
      setItems((xs) => xs.map((m) => (m.id === id ? { ...m, status: s } : m)));
      toast.success(`MARKED ${s.toUpperCase()}`);
    } catch {
      toast.error("UPDATE FAILED");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const newCount = items.filter((m) => m.status === "new").length;

  return (
    <div className="space-y-4" data-testid="admin-contact-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-mono text-xs text-neutral-500" data-testid="admin-contact-summary">
          {items.length} MESSAGES · {newCount} NEW · FROM /CONTACT FORM
        </div>
        <div className="flex items-center gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border-2 border-black bg-white px-2 py-1 font-mono text-xs" data-testid="admin-contact-filter">
            {STATUSES.map((s) => <option key={s} value={s}>{s || "all statuses"}</option>)}
          </select>
          <button onClick={load} className="btn-flat-outline text-xs" data-testid="admin-contact-refresh">
            <ArrowsClockwise size={14} weight="bold" /> REFRESH
          </button>
        </div>
      </div>

      <div className="border-2 border-black">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b-2 border-black bg-neutral-100 label-mono text-xs">
          <div className="col-span-3">WHEN</div>
          <div className="col-span-3">FROM</div>
          <div className="col-span-3">TOPIC</div>
          <div className="col-span-2">PREVIEW</div>
          <div className="col-span-1 text-right">STATUS</div>
        </div>
        {loading && <div className="px-3 py-6 label-mono text-neutral-500 text-center">LOADING…</div>}
        {!loading && items.length === 0 && (
          <div className="px-3 py-8 label-mono text-neutral-500 text-center" data-testid="admin-contact-empty">NO MESSAGES.</div>
        )}
        {!loading && items.map((m) => <MessageRow key={m.id} m={m} onStatus={setMsgStatus} />)}
      </div>
    </div>
  );
}
